import {
  useUpsertCompanyIntegrationMutation,
  useInitJohnDeereConnectionMutation,
  InitJohnDeereConnectionResponse,
} from '@/generated/codegen';
import useLocalSetting from '@/hooks/useLocalSetting';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import EmptyLayout from '@/components/Layout/EmptyLayout';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { JD_OAUTH_CALLBACK_URL } from '@/amplify';
import { toast } from 'sonner';

const JD_STATE_STORE = 'JDAuthState';
const CLIENT_ID = '0oaaprybc5HaS3n7z5d7';
const WELL_KNOWN_SERVER =
  'https://signin.johndeere.com/oauth2/aus78tnlaysMraFhC1t7/.well-known/oauth-authorization-server';

export const useMakeJohnDeereConnection = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setJDState] = useLocalSetting(JD_STATE_STORE, {});
  const location = useLocation();

  const startConnection = React.useCallback(
    async (companyId: number) => {
      const stateKey = uuid();
      const wellKnownReponse = await fetch(WELL_KNOWN_SERVER);
      const { authServer, tokenServer } = await wellKnownReponse
        .json()
        .then((json) => ({
          authServer: json['authorization_endpoint'],
          tokenServer: json['token_endpoint'],
        }));
      setJDState({
        authServer,
        tokenServer,
        stateKey,
        location,
        companyId,
        step: 1,
      });
      window.location.replace(
        authServer +
          '?' +
          new URLSearchParams({
            client_id: CLIENT_ID,
            // client_secret: CLIENT_SECRET,
            response_type: 'code',
            scope: 'eq1 offline_access',
            redirect_uri: JD_OAUTH_CALLBACK_URL,
            state: stateKey,
          })
      );
    },
    [location, setJDState]
  );
  return { startConnection };
};

export const JDOauthCallbackHandler = () => {
  const [JDState, setJDState] = useLocalSetting<{
    authServer: string;
    tokenServer: string;
    stateKey: string;
    location: Location;
    code?: string;
    access_token?: string;
    codeChallenge?: string;
    codeVerifier?: string;
    orgAccessRedirectUri?: string;
    companyId: number;
    step: number;
    credentials?: Omit<
      InitJohnDeereConnectionResponse,
      '__typename' | 'org_connection_redirect_url'
    >;
  }>(JD_STATE_STORE);

  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const stateKey = params.get('state');
  const [initJDConnection] = useInitJohnDeereConnectionMutation();
  const [upsertCompanyIntegration] = useUpsertCompanyIntegrationMutation();
  const handleAuthError = React.useCallback(
    (e: Error) => {
      toast.error(
        'Hubo un error intentando registrar la conexión con John Deere: ' +
          JSON.stringify(e)
      );
      history.push(JDState?.location ?? '/');
    },
    [JDState?.location, history]
  );
  const [feedbackMessage, setFeedbackMessage] = React.useState<string>();

  React.useEffect(() => {
    try {
      if (!JDState) {
        handleAuthError(
          new Error('no se pudo encontrar una transacción en curso')
        );
      } else if (JDState.step === 1) {
        if (!code || !stateKey || !JDState || JDState.stateKey !== stateKey) {
          toast.error('No recibimos los parámetros correctos');
          console.error({ code, stateKey, JDState, location: window.location });
          history.push('/');
          return;
        }
        setFeedbackMessage('Obteniendo código de autorización');
        setJDState((prevState) => ({
          ...prevState!,
          code: code,
          step: 2,
        }));
        initJDConnection({
          variables: { company_id: JDState.companyId, auth_code: code },
        })
          .then((response) => {
            if (!response.data?.initJohnDeereConnection) {
              setFeedbackMessage(
                'No hay conexiones por aceptar, procediendo a registrar la conexión en Bilderit Connect.'
              );
              setJDState((prevState) => ({
                ...prevState!,
                step: 5,
              }));
            } else {
              const { __typename, ...credentials } =
                response.data?.initJohnDeereConnection ?? {};
              setJDState((prevState) => ({
                ...prevState!,
                credentials: credentials ?? undefined,
                orgAccessRedirectUri:
                  response.data?.initJohnDeereConnection
                    ?.org_connection_redirect_url ?? '',
                code: code,
                step: 3,
              }));
            }
          })
          .catch(handleAuthError);
      } else if (JDState.step === 3) {
        setFeedbackMessage(
          'Intercambiando código de autorización por token de acceso'
        );
        setJDState((prevState) => ({
          ...prevState!,
          step: 5,
        }));
        if (JDState.orgAccessRedirectUri) {
          window.location.replace(
            JDState.orgAccessRedirectUri +
              '?redirect_uri=' +
              encodeURIComponent(JD_OAUTH_CALLBACK_URL)
          );
        }
      } else if (JDState.step === 5) {
        setFeedbackMessage('Registrando integración con Bilderit Connect');
        upsertCompanyIntegration({
          variables: {
            input: {
              company_id: JDState.companyId,
              provider: 'JOHN_DEERE',
              is_active: true,
              credentials: JDState.credentials,
            },
          },
        })
          .then(() => {
            toast.success('Integración registrada con éxito', {
              duration: 3000,
            });
            history.push(JDState.location);
          })
          .catch(handleAuthError);
      }
    } catch (e) {
      handleAuthError(e as Error);
    }
  }, [
    JDState,
    upsertCompanyIntegration,
    code,
    handleAuthError,
    history,
    initJDConnection,
    setJDState,
    stateKey,
  ]);

  return (
    <EmptyLayout>
      <div className="w-4/5 md:w-1/3 min-w-80 max-w-lg m-auto content-center">
        <LoadingMessageCard
          message={feedbackMessage ?? 'Procesando integración'}
        />
      </div>
    </EmptyLayout>
  );
};
